import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Assessment } from "@/components/portal/Assessment";
import { TraitBar } from "@/components/portal/Primitives";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { track } from "@/lib/analytics";
import { BIG_FIVE_ITEMS, BIG_FIVE_TRAITS, TRAIT_LABELS, TRAIT_DESCRIPTIONS, calculateBigFiveScores } from "@/data/questionnaires";

export default function BigFivePage() {
  const navigate = useNavigate();
  const { setUser } = usePortalAuth();
  const [submitting, setSubmitting] = useState(false);
  const [scores, setScores] = useState(null);

  const complete = async (responses) => {
    setSubmitting(true);
    const calculated = calculateBigFiveScores(responses);
    try {
      const updated = await portalApi.updateProfile({ big_five_completed: true, big_five_scores: calculated });
      setUser(updated);
      setScores(calculated);
      track("assessment_completed", { assessment: "big_five" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (scores) {
    return (
      <PortalLayout>
        <div data-testid="big-five-results">
          <div className="mb-10">
            <CheckCircle2 className="text-[#D4AF37] mb-5" size={34} />
            <h1 className="font-serif text-3xl md:text-4xl text-white mb-3">Your personality profile</h1>
            <p className="text-slate-400 text-sm max-w-2xl leading-relaxed">
              These five percentiles are one half of your vulnerability analysis. The other half is your leader's dark triad profile.
            </p>
          </div>
          <div className="space-y-5">
            {BIG_FIVE_TRAITS.map((trait, idx) => {
              const value = scores[trait];
              const desc = value >= 50 ? TRAIT_DESCRIPTIONS[trait].high : TRAIT_DESCRIPTIONS[trait].low;
              return (
                <div
                  key={trait}
                  className="border border-slate-800 bg-[#0B1220]/80 p-6"
                  style={{ animation: `fadeInUp 0.4s ease-out ${idx * 80}ms both` }}
                  data-testid={`big-five-result-${trait}`}
                >
                  <TraitBar label={TRAIT_LABELS[trait]} value={value} />
                  <p className="text-slate-400 text-sm mt-4 leading-relaxed">{desc}</p>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => navigate("/portal")}
            data-testid="big-five-continue-btn"
            className="mt-9 inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
          >
            Continue <ArrowRight size={14} />
          </button>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout back="/portal">
      <Assessment
        items={BIG_FIVE_ITEMS}
        eyebrow="Assessment 1 of 2"
        title="Big Five personality inventory"
        intro="Answer honestly about how you generally are — not how you wish you were. There are no good or bad results here; the point is to find which of your traits a toxic leader can use as leverage."
        submitting={submitting}
        onComplete={complete}
        testidPrefix="big-five"
      />
    </PortalLayout>
  );
}
